import React from 'react';
import 'highlight.js/styles/atom-one-dark.css';
import { Link } from '@inertiajs/react';
import { HiEye, HiDatabase, HiCurrencyDollar, HiServer, HiEmojiHappy, HiClipboard } from 'react-icons/hi';
import AnimHero from '../Components/animhero.jsx';

const features = [
  {
    title: "Local DICOM Store",
    desc: "Store and manage DICOM files locally in your browser.",
    icon: <HiDatabase />
  },
  {
    title: "Local Preview",
    desc: "Preview images and segmentations locally.",
    icon: <HiEye />
  },
  {
    title: "Pay-as-you-go Pricing",
    desc: "No monthly subscription. Run a model per image at a fixed rate.",
    icon: <HiCurrencyDollar />
  },
  {
    title: "Effortless Execution",
    desc: "Run open-source models from MHub.ai without local setup on GPU equipped cloud servers.",
    icon: <HiServer />
  },
  {
    title: "Clean and Intuitive UI",
    desc: "User-friendly interface for managing files, running models, and viewing results.",
    icon: <HiEmojiHappy />
  },
  {
    title: "Request Model Access",
    desc: "Easily request access to additional models and features.",
    icon: <HiClipboard />
  }
];

const faqs = [
  {
    question: "Do I need a subscription?",
    answer: "No. MCloud uses simple pay-as-you-go billing at 0.25€ per model execution per image."
  },
  {
    question: "Do I need to pay to create an account?",
    answer: "No, you can create an account for free."
  }, 
  {
    question: "Do I pay for using the dicom store?",
    answer: "No, using the local dicom store is available at no cost in your free account."
  },
  {
    question: "When are images uploaded to the cloud?",
    answer: "Images are only uploaded when you submit them for processing with a model. The image is deleted after processing. The result is stored online for 14 days or until downloaded."
  },
  {
    question: "Do I need to anonymize images before uploading?",
    answer: "Yes, you must ensure all images are anonymized before uploading."
  },
  {
    question: "Can I anonymize images using MCloud?",
    answer: "No, for now you need to anonymize images yourself before adding them to the dicom store. We are working on a solution to anonymize images within your browser."
  }
];

const Header = () => (
  <header className="sticky top-0 z-50 w-full backdrop-filter backdrop-blur-xl"> { /* bg-gray-900/20 */ }
    <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
      <Link href="/" className="flex items-center space-x-3">
        <img src="/images/mcloud_logo.png" alt="MCloud Logo" className="h-10" />
      </Link>
      <nav className="flex items-center space-x-8">
        <a href="https://mhub.ai" target="_blank" rel="noopener noreferrer" className="text-md text-gray-300 hover:text-emerald-400 hidden sm:inline-block">
          visit MHub.ai
        </a>
        {/* <Link href="/models" className="text-sm text-gray-300 hover:text-emerald-400">Models</Link> */}
        {/* <Link href="/pricing" className="text-sm text-gray-300 hover:text-emerald-400">Pricing</Link> */}
        {/* <Link href="/faq" className="text-sm text-gray-300 hover:text-emerald-400">Questions & Answers</Link> */}
        <Link href="/login" className="ml-2 inline-block px-4 py-2 bg-emerald-500 hover:bg-emerald-400 rounded text-black font-semibold">
          Launch App
        </Link>
      </nav>
    </div>
  </header>
);

const Feature = ({ title, desc, icon }) => (
  <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
    <div className="flex items-center space-x-4 mb-4">
      <div className="w-12 h-12 flex-none text-2xl flex items-center justify-center rounded-md bg-emerald-600/20 text-emerald-400">{icon}</div>
      <h3 className="text-lg font-semibold">{title}</h3>
    </div>
    <p className="text-gray-300 text-md">{desc}</p>
  </div>
);

const Hero = () => (
  <section className="grid md:grid-cols-2 gap-10 items-center mb-24 max-w-6xl mx-auto px-8">
    <div>
      <h2 className="text-4xl font-extrabold leading-tight">Effortless Research with <br/> MHub.ai in the cloud.</h2>
      <p className="text-gray-300 mt-4 text-lg max-w-2xl">Enable simple, fast and reproducible research projects. Manage DICOM files locally and run models on our managed infrastructure with one click.</p>

      <div className="mt-6 flex items-center space-x-3">
        <Link href="/login" className="px-5 py-3 bg-emerald-500 rounded text-black font-semibold hover:bg-emerald-400">Get started — Sign up for free</Link>
        {/* <Link href="/pricing" className="px-5 py-3 border border-gray-700 rounded text-gray-300">See pricing</Link> */}
      </div>

      {/* <div className="mt-10 grid grid-cols-2 gap-4 text-sm text-gray-400 max-w-sm">
        <div className="bg-gray-800/40 rounded p-3">Pay as you go</div>
        <div className="bg-gray-800/40 rounded p-3">Local DICOM store</div>
        <div className="bg-gray-800/40 rounded p-3">In-app preview</div>
        <div className="bg-gray-800/40 rounded p-3">Secure file management</div>
      </div> */}
    </div>

    <div className="rounded-lg overflow-hidden hidden md:block"> { /* bg-gray-900 */ }
      <div className="w-full rounded-md flex items-center justify-center">
        {/* <img src="/images/landing_animation.png" alt="App preview" className="object-cover h-full" /> */}
        <AnimHero className="object-cover h-full" />
      </div>
    </div>
  </section>
);

const PrimaryFeatures = () => (
  <section className="mb-20 max-w-6xl mx-auto px-8">
    <h3 className="text-2xl font-bold mb-6">Features</h3>
    <div className="grid md:grid-cols-3 gap-10">
      {features.map((feature) => (
        <Feature key={feature.title} title={feature.title} desc={feature.desc} icon={feature.icon} />
      ))}
    </div>
  </section>
);

// const PricingBox = () => (
//   <div className="max-w-md mx-auto">
//     <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-white/6 to-white/3 border border-gray-700 shadow-lg">
//       {/* Top: price block */}
//       <div className="p-6 bg-gradient-to-br from-emerald-600 to-emerald-500 text-black">
//         <div className="flex items-center justify-center">
//           <div className="inline-flex items-center px-4 py-1 rounded-full bg-black/20 text-sm font-extrabold tracking-wide">Pay as you go</div>
//         </div>

//         <div className="mt-6 flex items-baseline justify-center">
//           <span className="text-2xl font-medium text-white mr-2">€</span>
//           <span className="text-5xl md:text-6xl font-extrabold leading-tight text-white">0.25</span>
//           <span className="ml-3 text-sm text-emerald-100/90 self-end">per execution</span>
//         </div>

//         <p className="mt-3 text-xs text-emerald-100/80 text-center">Charged per image · per model · billed per execution</p>
//       </div>

//       {/* Bottom: features + CTA */}
//       <div className="p-5 bg-gray-900/50 border-t border-gray-800 flex flex-col gap-4">
//         <ul className="grid grid-cols-1 gap-3">
//           <li className="flex items-start gap-3">
//             <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500 text-black">
//               <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
//                 <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15 4.293 10.879a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" clipRule="evenodd" />
//               </svg>
//             </span>
//             <div>
//               <div className="text-sm font-semibold text-gray-200">No subscription</div>
//               <div className="text-xs text-gray-400">Pay only for executions you run.</div>
//             </div>
//           </li>

//           <li className="flex items-start gap-3">
//             <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500 text-black">
//               <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
//                 <path d="M3 7v10a2 2 0 002 2h10a2 2 0 002-2V7" stroke="currentColor" strokeWidth="0" />
//                 <path d="M8 3h4v4H8z" stroke="currentColor" strokeWidth="0" />
//               </svg>
//             </span>
//             <div>
//               <div className="text-sm font-semibold text-gray-200">Transparent billing</div>
//               <div className="text-xs text-gray-400">Billed per image · per model · per execution.</div>
//             </div>
//           </li>

//           <li className="flex items-start gap-3">
//             <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500 text-black">
//               <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
//                 <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
//               </svg>
//             </span>
//             <div>
//               <div className="text-sm font-semibold text-gray-200">Scale on demand</div>
//               <div className="text-xs text-gray-400">Run more when you need to, pause when you don't.</div>
//             </div>
//           </li>
//         </ul>

//         <div className="mt-1 flex justify-center">
//           <a href="/app" className="w-full md:w-auto inline-flex items-center justify-center px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-lg shadow-sm">Launch App — Try a sample</a>
//         </div>
//       </div>
//     </div>
//   </div>
// );

// const PricingCalculator = () => {
//   const presets = [
//     { id: 'starter', label: 'Starter', images: 10, models: 1 },
//     { id: 'growth', label: 'Growth', images: 100, models: 2 },
//     { id: 'scale', label: 'Scale', images: 1000, models: 3 },
//   ];

//   const [images, setImages] = React.useState(presets[0].images);
//   const [models, setModels] = React.useState(presets[0].models);
//   const [activePreset, setActivePreset] = React.useState(presets[0].id);

//   const pricePerExecution = 0.25;
//   const totalExecutions = Math.max(0, images) * Math.max(0, models);
//   const cost = (totalExecutions * pricePerExecution).toFixed(2);

//   const applyPreset = (p) => {
//     setImages(p.images);
//     setModels(p.models);
//     setActivePreset(p.id);
//   };

//   return (
//     <div className="rounded-lg border border-gray-700 p-6 bg-gray-800/40">
//       <h5 className="text-lg font-bold mb-4">Pricing calculator</h5>

//       <div className="space-y-4">
//         <div className="flex items-center space-x-3">
//           {presets.map((p) => (
//             <button
//               key={p.id}
//               onClick={() => applyPreset(p)}
//               className={`text-sm px-3 py-1 rounded-full transition-colors duration-150 ${activePreset === p.id ? 'bg-emerald-600 text-black' : 'bg-gray-900/30 text-gray-300 hover:bg-gray-900/50'}`}
//             >
//               {p.label}
//             </button>
//           ))}
//         </div>

//         <div>
//           <label className="flex items-center justify-between text-sm text-gray-300">
//             <span>Images per month</span>
//             <span className="font-mono text-gray-100">{images.toLocaleString()}</span>
//           </label>
//           <input
//             type="range"
//             min="0"
//             max="10000"
//             step="10"
//             value={images}
//             onChange={(e) => { setImages(Number(e.target.value)); setActivePreset(null); }}
//             className="w-full mt-2 accent-emerald-500"
//             aria-label="Images per month"
//           />

//           <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
//             <span>0</span>
//             <span>10k</span>
//             <span>100k</span>
//           </div>
//         </div>

//         <div>
//           <label className="flex items-center justify-between text-sm text-gray-300">
//             <span>Models per image</span>
//             <span className="font-mono text-gray-100">{models}</span>
//           </label>
//           <input
//             type="range"
//             min="1"
//             max="10"
//             step="1"
//             value={models}
//             onChange={(e) => { setModels(Number(e.target.value)); setActivePreset(null); }}
//             className="w-full mt-2 accent-emerald-500"
//             aria-label="Models per image"
//           />

//           <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
//             <span>1</span>
//             <span>5</span>
//             <span>10</span>
//           </div>
//         </div>

//         <div className="pt-3 border-t border-gray-700/40">
//           <div className="flex items-center justify-between">
//             <div className="text-sm text-gray-300">Total executions</div>
//             <div className="font-mono text-gray-100">{totalExecutions.toLocaleString()}</div>
//           </div>

//           <div className="flex items-center justify-between mt-3">
//             <div className="text-sm text-gray-300">Estimated monthly cost</div>
//             <div className="text-xl font-extrabold">€{cost}</div>
//           </div>

//           <div className="text-xs text-gray-400 mt-3">Estimated only. Storage, taxes and other fees not included.</div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const CallToAction_old = () => (
//   <section className="mb-20 max-w-6xl mx-auto px-8">
//     {/* <h3 className="text-2xl font-bold mb-6">Simple, transparent pricing</h3> */}
//     <div className="grid md:grid-cols-2 gap-8 items-start">
//       <PricingBox />
//       <PricingCalculator />
//     </div>
//   </section>
// );

const CallToAction = () => (
  <section className="mb-20 max-w-6xl mx-auto px-8">
    <div className="md:bg-gray-800/40 rounded-xl md:border border-gray-700 p-6 md:p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-center ">

      {/* Left: compact pricing card (visually distinct) */}
      <div className="md:col-span-1 flex justify-center lg:justify-start">
        <div className="w-full max-w-sm">
          <div className="w-full rounded-2xl p-8 shadow-[0_30px_80px_rgba(16,185,129,0.12)] border border-emerald-600 bg-gradient-to-br from-emerald-600 to-emerald-500 text-black">
            <div className="text-center">
              <div className="inline-flex items-center justify-center px-6 py-2 rounded-full bg-gray-800 text-white font-extrabold text-base tracking-wider">Pay Per Execution</div>

              <div className="mt-5 flex items-baseline justify-center">
                <span className="text-2xl font-medium text-white mr-2" aria-hidden>€</span>
                <span className="text-6xl font-extrabold leading-tight tracking-tight text-white">0.25</span>
                <span className="ml-3 text-sm text-emerald-100/90">per credit</span>
              </div>

              <p className="mt-3 text-xs text-emerald-100/80">1 credit per image and per model execution · minimum order amount 20 credits · taxes are applied at checkout</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right: explanation and checklist (wider column) */}
      <div className="lg:col-span-2 pl-0 lg:pl-8">
        <h3 className="text-xl font-semibold mb-2 text-emerald-300">Simple pricing</h3>
        <p className="text-gray-300 mb-4">Start with a free account. Purchase credits to process images using our cloud infrastructure. Running any model consumes 1 credit per image.</p>

        <ul className="space-y-4 text-sm text-gray-200 max-w-md">
          <li className="flex items-start space-x-3">
            <span className="mt-1 flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-black">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15 4.293 10.879a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </span>
            <div>
              <div className="font-semibold text-gray-200">No subscription required</div>
              <div className="text-xs text-gray-400">Pay only for executions you run.</div>
            </div>
          </li>

          <li className="flex items-start space-x-3">
            <span className="mt-1 flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-black">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15 4.293 10.879a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </span>
            <div>
              <div className="font-semibold text-gray-200">Usage-based billing</div>
              <div className="text-xs text-gray-400">Billed per image · per model · per execution.</div>
            </div>
          </li>

          <li className="flex items-start space-x-3">
            <span className="mt-1 flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-black">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15 4.293 10.879a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </span>
            <div>
              <div className="font-semibold text-gray-200">Transparent pricing</div>
              <div className="text-xs text-gray-400">Data transfer and short-term storage included.</div>
            </div>
          </li>
        </ul>
        {/* <p className="text-xs text-gray-500 mt-4">Estimated costs exclude taxes and storage. Contact sales for volume discounts and enterprise plans.</p> */}
      </div>
    </div>
  </section>
);


const Faqs = () => (
  <section className="mb-20 max-w-6xl mx-auto px-8">
    <h3 className="text-2xl font-bold mb-6">Frequently asked questions</h3>
    <div className="grid md:grid-cols-3 gap-6 text-gray-300">
      {faqs.map((faq) => (
        <div key={faq.question}>
          <h4 className="text-lg font-semibold mb-1">{faq.question}</h4>
          <p className="text-md text-gray-400">{faq.answer}</p>
        </div>
      ))}
    </div>
  </section>
);

const Disclaimer = () => (
  <div className="max-w-3xl mx-auto text-xs text-gray-500 mt-12 text-center">
    <p>
    Not for medical use — for research, demonstration and experimental purposes only.
    MCloud does not provide models and is not associated with model providers; it only provides access to compute resources for standardized open source models.
    Users must ensure compliance with all applicable laws and regulations when providing images to MCloud and when running AI models, and must only use anonymized data.
    </p>
  </div>
);

const Footer = () => (
  <footer className="border-t border-gray-800/60 py-12 mt-12">
    <div className="max-w-6xl mx-auto px-6 flex items-center justify-between text-gray-400">
      <div>© {new Date().getFullYear()} MCloud</div>
      <div className="flex items-center space-x-4">
        <Link href="/legal/agb" className="text-sm hover:text-emerald-400">Terms of Service</Link>
        <a href="/legal/privacy" className="text-sm hover:text-emerald-400">Privacy Policy</a>
        <a href="/legal/impressum" className="text-sm hover:text-emerald-400">Impressum</a>
      </div>
    </div>
  </footer>
);

export function Site({ children, showHeader = true }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-900 to-gray-900 text-gray-100 flex flex-col relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.05),transparent_20%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.05),transparent_40%)]"></div>
      <div className="relative z-10 flex flex-col flex-1">
        { showHeader && <Header /> }
        <main className="flex-1 max-w-6xl mx-auto w-full px-0 sm:px-8 py-8">
          {children}
        </main>
        <Footer />
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <CallToAction />
      <PrimaryFeatures />
      <Faqs />
      <Disclaimer />
    </>
  );
}

Home.layout = (page) => <Site>{page}</Site>;